import * as React from "react";
import {
    Segment,
    Header,
    Message,
    Dropdown,
    Grid,
    Table,
    Label,
    Button,
    Divider,
    Icon,
    Accordion,
    Checkbox,
} from "semantic-ui-react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    LineChart,
    Line,
} from "recharts";
import {authentication} from "../../../../../authentication";

/** Special dropdown value: compare every developer on one chart */
const ALL_DEVELOPERS = "__ALL__";

/** Mirrors docs/reports/dev_report/data/*.json from weekly_report_export.kt */
export interface DevWeeklyReportJson {
    schemaVersion: number;
    year: number;
    week: number;
    weekStart: string;
    weekEnd: string;
    dsOsRows: Array<{
        id: string;
        label: string;
        devDs: number;
        ois: number;
        cos: number;
        os: number;
        ticketIds: number[];
        isSeparator?: boolean;
    }>;
    developers: Array<{
        developer: string;
        testsTouched: number;
        /** Present from newer JSON exports; older files may omit */
        uniqueFilesByProject?: Array<{project: string; fileCount: number}>;
        currentWeek: string[];
        inProgress: string[];
        review: string[];
        mergeNeeded: string[];
        nextRelease: string[];
        closed: string[];
    }>;
    developerStoryPoints: Array<{
        developer: string;
        devDs: number;
        ois: number;
        cos: number;
        os: number;
        ticketCount: number;
    }>;
}

interface WeekIndexEntry {
    year: number;
    week: number;
    file: string;
    label: string;
}

interface WeekIndex {
    schemaVersion: number;
    weeks: WeekIndexEntry[];
}

/** Older JSON may still carry German labels from earlier exports */
const DS_OS_LABEL_LEGACY_EN: Record<string, string> = {
    "Unassigned (neu im Backlog)": "Unassigned (new in backlog)",
    CurrentWeek: "Current week",
    InProgress: "In progress",
    MergeNeeded: "Merge needed",
    NextRelease: "Next release",
    "✓ Bereit zum Review diese KW": "✓ Ready for review (this week)",
    "✓ NextRelease + Closed diese KW": "✓ Next release + closed (this week)",
    "* Bereit zum Test durch einen QA": "* Ready for QA testing",
    "* Next Release + Closed Ohne SCR": "* Next release + closed without SCR",
    "✓ Ready for review (KW week)": "✓ Ready for review (this week)",
    "✓ Final release / closed (KW week)": "✓ Next release + closed (this week)",
};

function displayDsOsLabel(label: string): string {
    return DS_OS_LABEL_LEGACY_EN[label] ?? label;
}

/** Recharts default tooltip order is arbitrary; sort by value high → low */
function ComparisonTeamTooltip(props: {
    active?: boolean;
    label?: string;
    payload?: Array<{name?: string; value?: number; color?: string; dataKey?: string | number}>;
}) {
    const {active, label, payload} = props;
    if (!active || !payload?.length) {
        return null;
    }
    const sorted = [...payload].sort((a, b) => (Number(b.value) || 0) - (Number(a.value) || 0));
    return (
        <div
            style={{
                backgroundColor: "#fff",
                border: "1px solid #ccc",
                borderRadius: 4,
                padding: "10px 12px",
                fontSize: 12,
                boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
            }}
        >
            <div style={{fontWeight: 600, marginBottom: 8}}>{label}</div>
            {sorted.map((p) => (
                <div key={String(p.dataKey)} style={{color: p.color, marginBottom: 2}}>
                    {p.name}: {p.value}
                </div>
            ))}
        </div>
    );
}

/** Bar chart tooltip: sort series by value high → low */
function BarSnapshotTooltip(props: {
    active?: boolean;
    label?: string;
    payload?: Array<{name?: string; value?: number; color?: string; dataKey?: string | number}>;
}) {
    const {active, label, payload} = props;
    if (!active || !payload?.length) {
        return null;
    }
    const sorted = [...payload].sort((a, b) => (Number(b.value) || 0) - (Number(a.value) || 0));
    return (
        <div
            style={{
                backgroundColor: "#fff",
                border: "1px solid #ccc",
                borderRadius: 4,
                padding: "10px 12px",
                fontSize: 12,
                boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
            }}
        >
            <div style={{fontWeight: 600, marginBottom: 8}}>{label}</div>
            {sorted.map((p) => (
                <div key={String(p.dataKey)} style={{color: p.color, marginBottom: 2}}>
                    {p.name}: {p.value}
                </div>
            ))}
        </div>
    );
}

function metricLabelText(metric: "devDs" | "os"): string {
    return metric === "devDs" ? "DEV" : "OS (total)";
}

const COMPARISON_LINE_COLORS = [
    "#2185d0",
    "#21ba45",
    "#f2711c",
    "#db2828",
    "#6435c9",
    "#008391",
    "#e03997",
    "#a5673f",
    "#009c95",
    "#b5cc18",
    "#fbbd08",
    "#1b1c1d",
];

function normalizeName(s: string): string {
    return s.trim().toLowerCase().replace(/\s+/g, " ");
}

function guessDefaultDeveloper(developers: string[]): string | null {
    const u = authentication.user;
    if (!u) {
        return null;
    }
    const fullName =
        u.firstName || u.lastName ? [u.firstName, u.lastName].filter(Boolean).join(" ").trim() : "";
    const candidates = [fullName, u.email].filter(Boolean) as string[];
    for (const c of candidates) {
        const n = normalizeName(c);
        const hit = developers.find((d) => normalizeName(d).includes(n) || n.includes(normalizeName(d)));
        if (hit) {
            return hit;
        }
    }
    return null;
}

function sortedDeveloperNames(reports: DevWeeklyReportJson[]): string[] {
    const names = new Set<string>();
    reports.forEach((r) => r.developerStoryPoints.forEach((d) => names.add(d.developer)));
    return Array.from(names).sort();
}

export function DevWeeklyReportPage() {
    const [index, setIndex] = React.useState<WeekIndex | null>(null);
    const [reports, setReports] = React.useState<DevWeeklyReportJson[]>([]);
    const [loadError, setLoadError] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [weekLimit, setWeekLimit] = React.useState<number>(1);
    const [selectedDeveloper, setSelectedDeveloper] = React.useState<string | null>(null);
    const [comparisonMetric, setComparisonMetric] = React.useState<"devDs" | "os">("devDs");
    const [helpAccordionOpen, setHelpAccordionOpen] = React.useState(false);
    const [devAccordionOpen, setDevAccordionOpen] = React.useState<Record<string, boolean>>({});
    const [includeDevBreakdown, setIncludeDevBreakdown] = React.useState(false);
    const [selectedWeekLabels, setSelectedWeekLabels] = React.useState<string[]>([]);

    const isSuper = authentication.isSuperUser === true;

    const developerNames = React.useMemo(() => sortedDeveloperNames(reports), [reports]);

    const allWeekOptions = React.useMemo(() =>
        reports.map((r) => {
            const lbl = `${r.year}-W${String(r.week).padStart(2, "0")}`;
            const start = r.weekStart?.slice(0, 10) ?? "";
            return {key: lbl, text: `${lbl}  (Mon ${start})`, value: lbl};
        }),
    [reports]);

    /** Reports filtered to the weeks the user has chosen; empty selection = all loaded weeks. */
    const activeReports = React.useMemo(() => {
        if (!selectedWeekLabels.length) { return reports; }
        return reports.filter((r) =>
            selectedWeekLabels.includes(`${r.year}-W${String(r.week).padStart(2, "0")}`),
        );
    }, [reports, selectedWeekLabels]);

    const weekRangeLabel = React.useMemo(() => {
        if (activeReports.length === 0) {
            return "";
        }
        const first = activeReports[0];
        const last = activeReports[activeReports.length - 1];
        return `${first.year}-W${String(first.week).padStart(2, "0")} → ${last.year}-W${String(last.week).padStart(2, "0")}`;
    }, [activeReports]);

    React.useEffect(() => {
        let cancelled = false;
        async function load() {
            setLoading(true);
            setLoadError(null);
            try {
                const idxRes = await fetch("/dev_weekly_reports/index.json");
                if (!idxRes.ok) {
                    throw new Error(`index.json failed: ${idxRes.status}`);
                }
                const idx: WeekIndex = await idxRes.json();
                if (cancelled) {
                    return;
                }
                setIndex(idx);
                const sorted = [...(idx.weeks || [])].sort((a, b) =>
                    a.year !== b.year ? a.year - b.year : a.week - b.week,
                );
                const take = sorted.slice(-weekLimit);
                const loaded: DevWeeklyReportJson[] = [];
                for (const w of take) {
                    const r = await fetch(`/dev_weekly_reports/${w.file}`);
                    if (r.ok) {
                        loaded.push(await r.json());
                    }
                }
                if (!cancelled) {
                    setReports(loaded);
                    const devList = sortedDeveloperNames(loaded);
                    // Default to the team view so the page opens with team productivity focus.
                    // Individual developer selection is still available via the Developer dropdown.
                    setSelectedDeveloper((prev) => prev ?? ALL_DEVELOPERS);
                }
            } catch (e: any) {
                if (!cancelled) {
                    setLoadError(e?.message || String(e));
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }
        load();
        return () => {
            cancelled = true;
        };
    }, [weekLimit]);

    const developerOptions = React.useMemo(() => {
        const allOption = {
            key: ALL_DEVELOPERS,
            text: "All developers (comparison)",
            value: ALL_DEVELOPERS,
        };
        const rest = developerNames.map((d) => ({key: d, text: d, value: d}));
        return [allOption, ...rest];
    }, [developerNames]);

    const singleTrendData = React.useMemo(() => {
        if (!selectedDeveloper || selectedDeveloper === ALL_DEVELOPERS) {
            return [];
        }
        const dev = selectedDeveloper;
        return activeReports.map((r) => {
            const row = r.developerStoryPoints.find((x) => x.developer === dev);
            return {
                label: `${r.year}-W${String(r.week).padStart(2, "0")}`,
                devDs: row?.devDs ?? 0,
                ois: row?.ois ?? 0,
                cos: row?.cos ?? 0,
                os: (row?.ois ?? 0) + (row?.cos ?? 0),
                tickets: row?.ticketCount ?? 0,
            };
        });
    }, [activeReports, selectedDeveloper]);

    /** Wide rows: v0, v1, … aligned with developerNames */
    const comparisonTrendData = React.useMemo(() => {
        if (selectedDeveloper !== ALL_DEVELOPERS) {
            return [];
        }
        return activeReports.map((r) => {
            const row: Record<string, number | string> = {
                label: `${r.year}-W${String(r.week).padStart(2, "0")}`,
            };
            developerNames.forEach((name, i) => {
                const sp = r.developerStoryPoints.find((x) => x.developer === name);
                const val =
                    comparisonMetric === "devDs"
                        ? sp?.devDs ?? 0
                        : (sp?.ois ?? 0) + (sp?.cos ?? 0);
                row[`v${i}`] = val;
            });
            return row;
        });
    }, [activeReports, selectedDeveloper, developerNames, comparisonMetric]);

    /** Team velocity: one bar per loaded week — team aggregate, always visible */
    const teamVelocityBarData = React.useMemo(() => {
        return activeReports.map((r) => {
            const sp = r.developerStoryPoints;
            return {
                label: `${r.year}-W${String(r.week).padStart(2, "0")}`,
                devDs: sp.reduce((s, d) => s + d.devDs, 0),
                ois: sp.reduce((s, d) => s + d.ois, 0),
                cos: sp.reduce((s, d) => s + d.cos, 0),
                os: sp.reduce((s, d) => s + d.ois + d.cos, 0), // OS = OIS + COS by definition
            };
        });
    }, [activeReports]);

    const teamBarData = React.useMemo(() => {
        const last = activeReports[activeReports.length - 1];
        if (!last) {
            return [];
        }
        return last.developerStoryPoints
            .map((d) => ({name: d.developer, devDs: d.devDs, os: d.ois + d.cos}))
            .sort((a, b) =>
                comparisonMetric === "devDs" ? b.devDs - a.devDs : b.os - a.os,
            );
    }, [activeReports, comparisonMetric]);

    const allDevelopersTotals = React.useMemo(() => {
        if (!activeReports.length) {
            return [];
        }
        const totals = new Map<
            string,
            {developer: string; devDs: number; ois: number; cos: number; os: number; ticketCount: number}
        >();
        for (const r of activeReports) {
            for (const sp of r.developerStoryPoints) {
                const existing = totals.get(sp.developer);
                if (existing) {
                    existing.devDs += sp.devDs;
                    existing.ois += sp.ois;
                    existing.cos += sp.cos;
                    existing.os += sp.ois + sp.cos;
                    existing.ticketCount += sp.ticketCount;
                } else {
                    totals.set(sp.developer, {
                        ...sp,
                        os: sp.ois + sp.cos,
                    });
                }
            }
        }
        return [...totals.values()].sort((a, b) =>
            comparisonMetric === "devDs" ? b.devDs - a.devDs : b.os - a.os,
        );
    }, [activeReports, comparisonMetric]);

    /**
     * Primary status rows: mutually exclusive categories (unassigned, currentWeek, inProgress,
     * review, mergeNeeded, nextRelease, closed). These are additive — summing them gives the real
     * OS/DEV total for all tickets in the week.
     */
    const primaryDsOsRows = React.useMemo(() => {
        const last = activeReports[activeReports.length - 1];
        if (!last) {
            return [];
        }
        const PRIMARY_IDS = new Set([
            "unassigned",
            "currentWeek",
            "inProgress",
            "review",
            "mergeNeeded",
            "nextRelease",
            "closed",
        ]);
        return last.dsOsRows.filter((r) => PRIMARY_IDS.has(r.id));
    }, [activeReports]);

    /**
     * Sub-total highlight rows that come after the separator (readyReview, nextClosed).
     * These are SUBSETS of the primary rows — they MUST NOT be added to the primary total.
     */
    const summaryDsOsRows = React.useMemo(() => {
        const last = activeReports[activeReports.length - 1];
        if (!last) {
            return [];
        }
        const SUMMARY_IDS = new Set(["readyReview", "nextClosed"]);
        return last.dsOsRows.filter((r) => SUMMARY_IDS.has(r.id));
    }, [activeReports]);

    const latestDevelopersActivity = React.useMemo(() => {
        const last = activeReports[activeReports.length - 1];
        if (!last?.developers?.length) {
            return [];
        }
        const scoreByDev = new Map(
            last.developerStoryPoints.map((d) => [
                d.developer,
                comparisonMetric === "devDs" ? d.devDs : d.ois + d.cos,
            ]),
        );
        return [...last.developers].sort(
            (a, b) => (scoreByDev.get(b.developer) ?? 0) - (scoreByDev.get(a.developer) ?? 0),
        );
    }, [activeReports, comparisonMetric]);

    const uniqueFilesFlatRows = React.useMemo(() => {
        const rows: Array<{developer: string; project: string; fileCount: number}> = [];
        for (const d of latestDevelopersActivity) {
            const projects = d.uniqueFilesByProject ?? [];
            for (const p of projects) {
                rows.push({developer: d.developer, project: p.project, fileCount: p.fileCount});
            }
        }
        return rows;
    }, [latestDevelopersActivity]);

    const companyScopeTag =
        authentication.company?.tag?.trim() || authentication.company?.name?.trim() || null;

    if (loading) {
        return <Segment loading basic style={{minHeight: 200}} />;
    }

    if (loadError) {
        return (
            <Message negative>
                <Message.Header>Could not load dev weekly reports</Message.Header>
                <p>{loadError}</p>
                <p style={{marginTop: "0.75em"}}>
                    Ensure JSON exists under <code>docs/reports/dev_report/data/</code> and run a production build
                    (files are copied to <code>/dev_weekly_reports/</code>).
                </p>
            </Message>
        );
    }

    if (!index || !index.weeks || index.weeks.length === 0) {
        return (
            <Message info>
                <Message.Header>No weekly data yet</Message.Header>
                <p>
                    Run <code>./gradlew :xconvert-helpers:stats:run</code> or{" "}
                    <code>./scripts/run_dev_weekly_reports.sh</code>, then rebuild the config-manager.
                </p>
            </Message>
        );
    }

    const latestWeekTag =
        activeReports.length > 0
            ? `${activeReports[activeReports.length - 1].year}-W${String(activeReports[activeReports.length - 1].week).padStart(2, "0")}`
            : "";

    function toggleDevAccordion(key: string) {
        setDevAccordionOpen((prev) => ({...prev, [key]: !prev[key]}));
    }

    // ── SVG chart helper (no CDN, always renders) ───────────────────────────────
    function buildSvgChart(lbls: string[], devDs: number[], ois: number[], cos: number[]): string {
        const W = 820, H = 280;
        const pad = {top: 28, right: 20, bottom: 60, left: 56};
        const n = lbls.length;
        if (n === 0) { return `<svg viewBox="0 0 ${W} ${H}"></svg>`; }
        const chartW = W - pad.left - pad.right;
        const chartH = H - pad.top - pad.bottom;
        const maxVal = Math.max(...devDs, ...ois, ...cos, 1);
        const rawStep = maxVal / 5;
        const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
        const tickStep = Math.ceil(rawStep / mag) * mag;

        // 2 bar-groups per week: [DEV standalone] [OIS+COS stacked]
        const groupW  = chartW / n;
        const barW    = Math.max(4, Math.min(groupW * 0.28, 34));
        const barGap  = Math.max(2, barW * 0.2);
        const pairW   = barW * 2 + barGap;
        const groupGap = (groupW - pairW) / 2;

        // Y scale uses max of DEV or (OIS+COS) so bars don't overflow
        const maxVal2 = Math.max(...devDs, ...ois.map((v, i) => v + cos[i]), 1);
        const rawStep2 = maxVal2 / 5;
        const mag2 = Math.pow(10, Math.floor(Math.log10(rawStep2)));
        const tickStep2 = Math.ceil(rawStep2 / mag2) * mag2;
        const effectiveMax = maxVal2;

        let grid = "";
        for (let t = 0; t * tickStep2 <= effectiveMax + tickStep2; t++) {
            const val = t * tickStep2;
            const y = pad.top + chartH - (val / effectiveMax) * chartH;
            if (y < pad.top) { break; }
            grid += `<line x1="${pad.left}" y1="${y.toFixed(1)}" x2="${pad.left + chartW}" y2="${y.toFixed(1)}" stroke="#f0f3f7" stroke-width="1"/>`;
            grid += `<text x="${(pad.left - 5).toFixed(1)}" y="${(y + 4).toFixed(1)}" text-anchor="end" font-size="10" fill="#636e72">${val}</text>`;
        }

        let bars = "";
        for (let i = 0; i < n; i++) {
            const gx   = pad.left + groupW * i + groupGap;
            const baseY = pad.top + chartH;

            // DEV bar — standalone
            const devH = Math.max(0, (devDs[i] / effectiveMax) * chartH);
            if (devH > 0) {
                bars += `<rect x="${gx.toFixed(1)}" y="${(baseY - devH).toFixed(1)}" width="${barW.toFixed(1)}" height="${devH.toFixed(1)}" fill="#2185d0" rx="2"/>`;
                bars += `<text x="${(gx + barW / 2).toFixed(1)}" y="${(baseY - devH - 3).toFixed(1)}" text-anchor="middle" font-size="8.5" fill="#555">${devDs[i]}</text>`;
            }

            // OIS+COS stacked bar — OS is defined as OIS+COS, so total = ois[i]+cos[i]
            const x2   = gx + barW + barGap;
            const oiCo = ois[i] + cos[i];
            const cosH = Math.max(0, (cos[i] / effectiveMax) * chartH);
            const oisH = Math.max(0, (ois[i] / effectiveMax) * chartH);
            if (cosH > 0) {
                bars += `<rect x="${x2.toFixed(1)}" y="${(baseY - cosH).toFixed(1)}" width="${barW.toFixed(1)}" height="${cosH.toFixed(1)}" fill="#fbbd08" rx="2"/>`;
            }
            if (oisH > 0) {
                bars += `<rect x="${x2.toFixed(1)}" y="${(baseY - cosH - oisH).toFixed(1)}" width="${barW.toFixed(1)}" height="${oisH.toFixed(1)}" fill="#21ba45" rx="0"/>`;
            }
            if (oiCo > 0) {
                bars += `<text x="${(x2 + barW / 2).toFixed(1)}" y="${(baseY - cosH - oisH - 3).toFixed(1)}" text-anchor="middle" font-size="8.5" fill="#555">${oiCo}</text>`;
            }

            const cx = pad.left + groupW * i + groupW / 2;
            const lbl = lbls[i].replace("KW", "");
            bars += `<text x="${cx.toFixed(1)}" y="${(baseY + 14).toFixed(1)}" text-anchor="middle" font-size="9.5" fill="#636e72">${lbl}</text>`;
        }

        const axes = `<line x1="${pad.left}" y1="${pad.top}" x2="${pad.left}" y2="${pad.top + chartH}" stroke="#dfe6e9"/>` +
                     `<line x1="${pad.left}" y1="${pad.top + chartH}" x2="${pad.left + chartW}" y2="${pad.top + chartH}" stroke="#dfe6e9"/>`;

        const lgy = H - 14;
        const legend = [
            {c: "#2185d0", t: "DEV \u2014 Development Score"},
            {c: "#21ba45", t: "OIS \u2014 Operational Improvement"},
            {c: "#fbbd08", t: "COS \u2014 Customer Onboarding"},
        ].map((it, i) =>
            `<rect x="${pad.left + i * 220}" y="${lgy - 9}" width="11" height="10" fill="${it.c}" rx="2"/>` +
            `<text x="${pad.left + i * 220 + 15}" y="${lgy}" font-size="10.5" fill="#2d3436">${it.t}</text>`
        ).join("");

        return `<svg viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg" style="width:100%;height:auto;display:block;max-height:280px">${grid}${axes}${bars}${legend}</svg>`;
    }

    // ── Report HTML builder ──────────────────────────────────────────────────────
    function buildReportHtml(): string {
        const wkLabels   = teamVelocityBarData.map((w) => w.label);
        const wkDevDs    = teamVelocityBarData.map((w) => w.devDs);
        const wkOis      = teamVelocityBarData.map((w) => w.ois);
        const wkCos      = teamVelocityBarData.map((w) => w.cos);
        const wkOs       = teamVelocityBarData.map((w) => w.os);
        const totalDevDs = wkDevDs.reduce((s, v) => s + v, 0);
        const totalOis   = wkOis.reduce((s, v) => s + v, 0);
        const totalCos   = wkCos.reduce((s, v) => s + v, 0);
        const totalOs    = wkOs.reduce((s, v) => s + v, 0);
        const avgDevDs     = wkDevDs.length ? (totalDevDs / wkDevDs.length).toFixed(1) : "0";
        const latestDevDs  = wkDevDs[wkDevDs.length - 1] ?? 0;
        const prevDevDs    = wkDevDs.length >= 2 ? wkDevDs[wkDevDs.length - 2] : null;
        const trendDelta   = prevDevDs !== null ? latestDevDs - prevDevDs : null;
        const trendArrow   = trendDelta !== null ? (trendDelta >= 0 ? "&#9650;" : "&#9660;") : "";
        const trendColor   = trendDelta !== null ? (trendDelta >= 0 ? "#27ae60" : "#e74c3c") : "#888";
        const latestLabel  = wkLabels[wkLabels.length - 1] ?? "";
        const prevLabel    = wkLabels.length >= 2 ? wkLabels[wkLabels.length - 2] : "";
        const trendHtml    = trendDelta !== null
            ? `<span style="color:${trendColor}">${trendArrow} ${Math.abs(trendDelta)} pts vs ${prevLabel}</span>`
            : `<span style="color:#888">Only 1 week &mdash; no comparison</span>`;
        const now          = new Date().toLocaleString();
        const year         = activeReports.length ? activeReports[activeReports.length - 1].year : new Date().getFullYear();
        const unassignedOs = primaryDsOsRows.find((r) => r.id === "unassigned")?.os ?? 0;

        const chartSvg = buildSvgChart(wkLabels, wkDevDs, wkOis, wkCos);

        const dsOsRows = primaryDsOsRows.map((r) =>
            `<tr${r.id === "unassigned" ? ' class="row-warn"' : ""}>
                <td>${displayDsOsLabel(r.label)}</td>
                <td class="num">${r.devDs}</td><td class="num">${r.ois}</td>
                <td class="num">${r.cos}</td><td class="num">${r.os}</td></tr>`,
        ).join("");

        const weekRows = [...teamVelocityBarData].reverse().map((w, idx) =>
            `<tr class="${w.label === latestWeekTag ? "row-latest" : idx % 2 === 0 ? "row-even" : ""}">
                <td>${w.label}${w.label === latestWeekTag ? ' <span class="badge">latest</span>' : ""}</td>
                <td class="num">${w.devDs}</td><td class="num">${w.ois}</td>
                <td class="num">${w.cos}</td><td class="num">${w.os}</td></tr>`,
        ).join("");

        const devSection = includeDevBreakdown ? (() => {
            const rows = allDevelopersTotals.map((d, i) =>
                `<tr class="${i % 2 === 0 ? "row-even" : ""}"><td><strong>${d.developer}</strong></td>
                <td class="num">${d.devDs}</td><td class="num">${d.ois}</td>
                <td class="num">${d.cos}</td><td class="num">${d.os}</td><td class="num">${d.ticketCount}</td></tr>`,
            ).join("");
            return `<div class="section"><h2>Developer breakdown &mdash; reference only</h2>
            <p class="note">Individual scores summed over the selected period. Team total is the primary metric.</p>
            <table><thead><tr><th>Developer</th><th>DEV</th><th>OIS</th><th>COS</th><th>OS</th><th>Tickets</th></tr></thead>
            <tbody>${rows}</tbody>
            <tfoot><tr><td>Total</td>
            <td class="num">${allDevelopersTotals.reduce((s, r) => s + r.devDs, 0)}</td>
            <td class="num">${allDevelopersTotals.reduce((s, r) => s + r.ois, 0)}</td>
            <td class="num">${allDevelopersTotals.reduce((s, r) => s + r.cos, 0)}</td>
            <td class="num">${allDevelopersTotals.reduce((s, r) => s + r.os, 0)}</td>
            <td class="num">${allDevelopersTotals.reduce((s, r) => s + r.ticketCount, 0)}</td></tr></tfoot></table></div>`;
        })() : "";

        return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>${buildReportFilename("pdf").replace(".pdf", "")}</title>
<style>
:root{--blue:#2185d0;--green:#21ba45;--yellow:#fbbd08;--red:#db2828;--gray:#636e72;--border:#dfe6e9;--white:#fff;--light:#f5f6fa}
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;background:var(--light);color:#2d3436;font-size:14px;line-height:1.6}
#toolbar{background:#1a1a2e;color:#fff;padding:12px 28px;display:flex;align-items:center;gap:10px;position:sticky;top:0;z-index:99}
#toolbar h1{font-size:1rem;font-weight:600;flex:1;margin:0}
.btn{padding:8px 20px;border:none;border-radius:6px;cursor:pointer;font-size:13px;font-weight:600}
.btn-print{background:#e84393;color:#fff}.btn-dl{background:#636e72;color:#fff}
#content{padding:28px 32px;max-width:1100px;margin:0 auto}
.page-title{font-size:1.5rem;font-weight:700;color:#1a1a2e;margin-bottom:4px}
.meta{color:var(--gray);font-size:.85rem;margin-bottom:22px}
.kpi-row{display:grid;grid-template-columns:repeat(auto-fit,minmax(145px,1fr));gap:12px;margin-bottom:26px}
.kpi{background:var(--white);border-radius:10px;padding:15px 18px;box-shadow:0 2px 8px rgba(0,0,0,.06);border-left:4px solid var(--blue)}
.kpi.green{border-color:var(--green)}.kpi.yellow{border-color:var(--yellow)}.kpi.red{border-color:var(--red)}
.kpi .val{font-size:1.9rem;font-weight:800;letter-spacing:-.5px}.kpi .lbl{font-size:.75rem;color:var(--gray);margin-top:2px;text-transform:uppercase;letter-spacing:.3px}
.kpi .trend{font-size:.82rem;margin-top:4px}
.section{background:var(--white);border-radius:10px;padding:20px 24px;box-shadow:0 2px 8px rgba(0,0,0,.06);margin-bottom:22px}
.section h2{font-size:1rem;font-weight:700;color:#1a1a2e;margin-bottom:8px;padding-bottom:7px;border-bottom:2px solid var(--border)}
.note{font-size:.82rem;color:var(--gray);margin-bottom:10px}
table{width:100%;border-collapse:collapse;font-size:.87rem}
th{background:#f8f9fb;text-align:left;padding:8px 12px;font-weight:600;border-bottom:2px solid var(--border)}
td{padding:7px 12px;border-bottom:1px solid #f0f3f7}td.num{text-align:right;font-variant-numeric:tabular-nums;font-weight:500}
.row-even td{background:#fafbfc}.row-latest td{background:#eaf4ff}.row-warn td{background:#fff8e6}
tfoot td{border-top:2px solid #b2bec3;border-bottom:none;font-weight:700;background:#eaf4ff!important}
.badge{background:var(--blue);color:#fff;border-radius:3px;padding:1px 5px;font-size:.7rem;margin-left:4px}
.footer{text-align:center;color:#b2bec3;font-size:.75rem;margin-top:24px;padding-bottom:24px}
@media print{#toolbar{display:none!important}body{background:#fff}#content{padding:8px;max-width:none}.section,.kpi{box-shadow:none;border:1px solid var(--border);page-break-inside:avoid}@page{size:A4;margin:1.2cm}}
</style>
</head>
<body>
<div id="toolbar">
  <h1>&#128200; Team Velocity Report ${year}</h1>
  <button class="btn btn-print" onclick="window.print()">&#8681; Save as PDF / Print</button>
</div>
<div id="content">
  <p class="page-title">&#128200; Team Velocity &mdash; ${year}</p>
  <p class="meta">Period: <strong>${weekRangeLabel || latestWeekTag}</strong> &bull; ${activeReports.length} week${activeReports.length !== 1 ? "s" : ""} &bull; Work week: Mon&ndash;Fri (Sat/Sun tickets counted in next Mon) &bull; Generated: ${now}</p>
  <div class="kpi-row">
    <div class="kpi"><div class="val">${totalDevDs}</div><div class="lbl">Total DEV (${activeReports.length} week${activeReports.length !== 1 ? "s" : ""})</div></div>
    <div class="kpi green"><div class="val">${avgDevDs}</div>
      <div class="lbl">Avg DEV / week</div>
      <div class="trend" style="font-size:.78rem;color:#888;margin-top:3px">over ${activeReports.length} week${activeReports.length !== 1 ? "s" : ""} (${weekRangeLabel || latestLabel})</div></div>
    <div class="kpi yellow"><div class="val">${latestDevDs}</div>
      <div class="lbl">Latest week DEV (${latestLabel})</div>
      <div class="trend" style="margin-top:5px">${trendHtml}</div></div>
    <div class="kpi"><div class="val">${totalOs}</div><div class="lbl">Total OS (${activeReports.length} week${activeReports.length !== 1 ? "s" : ""})</div></div>
    <div class="kpi red"><div class="val">${unassignedOs}</div><div class="lbl">Unassigned OS (${latestLabel})</div></div>
  </div>
  <div class="section">
    <h2>Team velocity per calendar week</h2>
    <p class="note">DEV (Development Score) shown as its own bar. OIS (Operational Improvement) and COS (Customer Onboarding) stacked together as one bar for comparison.</p>
    ${chartSvg}
  </div>
  <div class="section">
    <h2>Weekly summary &mdash; team totals</h2>
    <table><thead><tr><th>Week</th><th>DEV</th><th>OIS</th><th>COS</th><th>OS</th></tr></thead>
    <tbody>${weekRows}</tbody>
    <tfoot><tr><td>Total (${activeReports.length} week${activeReports.length !== 1 ? "s" : ""})</td>
      <td class="num">${totalDevDs}</td><td class="num">${totalOis}</td>
      <td class="num">${totalCos}</td><td class="num">${totalOs}</td></tr></tfoot></table>
  </div>
  <div class="section">
    <h2>Ticket pipeline &mdash; latest week (${latestWeekTag})</h2>
    <p class="note">Each ticket belongs to exactly one status bucket. Total = true OS for the week including unassigned.</p>
    <table><thead><tr><th>Status</th><th>DEV</th><th>OIS</th><th>COS</th><th>OS</th></tr></thead>
    <tbody>${dsOsRows}</tbody>
    <tfoot><tr><td>Total (all tickets)</td>
      <td class="num">${primaryDsOsRows.reduce((s, r) => s + r.devDs, 0)}</td>
      <td class="num">${primaryDsOsRows.reduce((s, r) => s + r.ois, 0)}</td>
      <td class="num">${primaryDsOsRows.reduce((s, r) => s + r.cos, 0)}</td>
      <td class="num">${primaryDsOsRows.reduce((s, r) => s + r.os, 0)}</td></tr></tfoot></table>
  </div>
  ${devSection}
  <p class="footer">xconvert dev weekly report &mdash; ${now}</p>
</div>
</body></html>`;
    }

    // ── Print: hidden iframe approach (reliable in all browsers) ────────────────
    function handlePrintReport() {
        const html = buildReportHtml();
        const pdfName = buildReportFilename("pdf").replace(".pdf", "");

        const iframe = document.createElement("iframe");
        iframe.style.cssText = "position:fixed;top:-10000px;left:-10000px;width:1px;height:1px;border:0;visibility:hidden";
        document.body.appendChild(iframe);
        const doc = iframe.contentDocument ?? (iframe.contentWindow as any)?.document;
        if (!doc) { document.body.removeChild(iframe); return; }
        doc.open();
        doc.write(html);
        doc.close();

        // Browsers use the parent document.title as the suggested PDF filename —
        // temporarily override it so the Save As dialog shows the report name.
        const originalTitle = document.title;
        document.title = pdfName;

        setTimeout(() => {
            try {
                (iframe.contentWindow as any).focus();
                (iframe.contentWindow as any).print();
            } finally {
                // Restore original title after the print dialog has opened
                setTimeout(() => {
                    document.title = originalTitle;
                    if (document.body.contains(iframe)) document.body.removeChild(iframe);
                }, 2000);
            }
        }, 600);
    }

    // ── Shared filename helper ───────────────────────────────────────────────────
    function buildReportFilename(ext: "html" | "pdf"): string {
        const yr = activeReports.length
            ? activeReports[activeReports.length - 1].year
            : new Date().getFullYear();
        if (activeReports.length === 1) {
            return `velocity_${yr}_${latestWeekTag || "KW-??"}.${ext}`;
        }
        const firstTag = activeReports.length
            ? `${activeReports[0].year}-W${String(activeReports[0].week).padStart(2, "0")}`
            : "";
        return `velocity_${yr}_${firstTag}-to-${latestWeekTag || "KW-??"}.${ext}`;
    }

    // ── Download as HTML file ────────────────────────────────────────────────────
    function handleDownloadReport() {
        const html = buildReportHtml();
        const blob = new Blob([html], {type: "text/html;charset=utf-8"});
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = buildReportFilename("html");
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 5000);
    }

    return (
        <div style={{padding: "0.75rem 1rem 2rem 0.5rem", width: "100%", maxWidth: "100%", boxSizing: "border-box"}}>
            <Header as="h3" style={{marginBottom: "0.75rem"}}>
                <Icon name="chart line" />
                Dev weekly report
            </Header>

            {false && (
                <Accordion styled fluid style={{marginBottom: "1rem"}}>
                    <Accordion.Title active={helpAccordionOpen} onClick={() => setHelpAccordionOpen(!helpAccordionOpen)}>
                        <Icon name="dropdown" />
                        Historical JSON / backfill (~3 months)
                    </Accordion.Title>
                    <Accordion.Content active={helpAccordionOpen}>
                        <Message info size="small" style={{margin: 0}}>
                            <Message.List>
                                <Message.Item>
                                    From repo root, with <code>GITLAB_TOKEN</code> set:{" "}
                                    <code style={{whiteSpace: "pre-wrap"}}>
                                        chmod +x scripts/backfill_dev_weekly_reports.sh && BACKFILL_WEEKS=13
                                        ./scripts/backfill_dev_weekly_reports.sh
                                    </code>
                                </Message.Item>
                                <Message.Item>
                                    JSON-only (no extra <code>KW*.txt</code>): add <code>--json-only</code> to the script.
                                </Message.Item>
                                <Message.Item>
                                    Existing <code>docs/reports/dev_report/KW*.txt</code> files are not imported
                                    automatically; backfill recomputes each week from GitLab + git.
                                </Message.Item>
                            </Message.List>
                        </Message>
                    </Accordion.Content>
                </Accordion>
            )}

            <Segment raised style={{marginBottom: "1rem"}}>
                <Grid stackable columns={3} verticalAlign="bottom">
                    <Grid.Column width={5}>
                        <label style={{fontWeight: 600, display: "block", marginBottom: 6}}>
                            Weeks to load
                        </label>
                        <Dropdown
                            selection
                            value={weekLimit}
                            options={[
                                {value: 1, text: "Last week"},
                                {value: 2, text: "Last 2 weeks"},
                                {value: 4, text: "Last 4 weeks"},
                                {value: 8, text: "Last 8 weeks"},
                                {value: 12, text: "Last 12 weeks"},
                                {value: 9999, text: "All"},
                            ]}
                            onChange={(_, d) => {
                                setWeekLimit(Number(d.value));
                                setSelectedWeekLabels([]);
                            }}
                        />
                        {reports.length > 0 && (
                            <div style={{marginTop: 10}}>
                                <label style={{fontWeight: 600, display: "block", marginBottom: 4, fontSize: "0.9em"}}>
                                    Filter to specific week(s)
                                </label>
                                <Dropdown
                                    placeholder="All loaded weeks"
                                    multiple
                                    selection
                                    clearable
                                    options={allWeekOptions}
                                    value={selectedWeekLabels}
                                    onChange={(_, d) => setSelectedWeekLabels(d.value as string[])}
                                    style={{minWidth: "100%"}}
                                />
                                <div style={{fontSize: "0.8em", color: "#888", marginTop: 4}}>
                                    {selectedWeekLabels.length
                                        ? `Showing ${selectedWeekLabels.length} of ${reports.length} loaded week${reports.length !== 1 ? "s" : ""}`
                                        : `${reports.length} week${reports.length !== 1 ? "s" : ""} loaded (${weekRangeLabel})`}
                                </div>
                            </div>
                        )}
                    </Grid.Column>
                    {/* Developer filter hidden — individual developer view is in the accordion section below */}
                    {false && (
                        <Grid.Column width={5}>
                            <label style={{fontWeight: 600, display: "block", marginBottom: 6}}>Developer</label>
                            <Dropdown
                                selection
                                search
                                placeholder="Select developer or All"
                                value={selectedDeveloper || undefined}
                                options={developerOptions}
                                onChange={(_, d) => setSelectedDeveloper(d.value as string)}
                            />
                        </Grid.Column>
                    )}
                    <Grid.Column width={5}>
                        <label style={{fontWeight: 600, display: "block", marginBottom: 6}}>
                            Metric (table order)
                        </label>
                        <Button.Group size="small">
                            <Button
                                active={comparisonMetric === "devDs"}
                                onClick={() => setComparisonMetric("devDs")}
                            >
                                DEV
                            </Button>
                            <Button active={comparisonMetric === "os"} onClick={() => setComparisonMetric("os")}>
                                OS (total)
                            </Button>
                        </Button.Group>
                    </Grid.Column>
                    <Grid.Column width={6}>
                        <label style={{fontWeight: 600, display: "block", marginBottom: 6}}>Export</label>
                        <div style={{display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center"}}>
                            <Button
                                primary icon labelPosition="left"
                                onClick={handlePrintReport}
                                disabled={activeReports.length === 0}
                                size="small"
                            >
                                <Icon name="print" />
                                Print / Save PDF
                            </Button>
                            <Button
                                icon labelPosition="left"
                                onClick={handleDownloadReport}
                                disabled={activeReports.length === 0}
                                size="small"
                            >
                                <Icon name="download" />
                                Download HTML
                            </Button>
                        </div>
                        <Checkbox
                            label="Include developer breakdown in export"
                            checked={includeDevBreakdown}
                            onChange={(_, d) => setIncludeDevBreakdown(!!d.checked)}
                            style={{display: "block", marginTop: 8, fontSize: "0.85em"}}
                        />
                    </Grid.Column>
                </Grid>
            </Segment>

            {teamVelocityBarData.length > 0 && (
                <Segment raised style={{marginBottom: "1rem"}}>
                    <Header
                        as="h4"
                        style={{display: "flex", alignItems: "center", flexWrap: "wrap", gap: "0.75rem"}}
                    >
                        Team velocity
                        <Label color="blue" size="small">DEV per week</Label>
                        {companyScopeTag && (
                            <Label color="red" size="small">
                                {companyScopeTag}
                            </Label>
                        )}
                    </Header>
                    <p style={{color: "#666", marginBottom: "0.4rem"}}>
                        Team score per calendar week — <strong>DEV</strong> (Development) shown independently.{" "}
                        <strong>OIS</strong> (Operational Improvement) + <strong>COS</strong> (Customer Onboarding) stacked together.
                    </p>
                    <p style={{color: "#aaa", fontSize: "0.82em", marginBottom: "0.75rem"}}>
                        Work week: Mon–Fri. Tickets created on Sat/Sun count toward the following week.
                    </p>
                    <ResponsiveContainer width="100%" height={340}>
                        <BarChart
                            data={teamVelocityBarData}
                            margin={{top: 8, right: 16, left: 0, bottom: 0}}
                            barCategoryGap="15%"
                            barGap={3}
                        >
                            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                            <XAxis dataKey="label" tick={{fontSize: 11}} />
                            <YAxis tick={{fontSize: 11}} />
                            <Tooltip contentStyle={{fontSize: 12}} />
                            <Legend />
                            <Bar dataKey="devDs" name="DEV (Development)" fill="#2185d0" radius={[3, 3, 0, 0]} />
                            <Bar dataKey="cos" name="COS (Customer Onboarding)" fill="#fbbd08" stackId="operational" radius={[0, 0, 0, 0]} />
                            <Bar dataKey="ois" name="OIS (Operational)" fill="#21ba45" stackId="operational" radius={[3, 3, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </Segment>
            )}

            {/* ── DS/OS pipeline overview — team-level, always visible ────────────────── */}
            {primaryDsOsRows.length > 0 && (() => {
                const totalDevDs = primaryDsOsRows.reduce((s, r) => s + r.devDs, 0);
                const totalOis   = primaryDsOsRows.reduce((s, r) => s + r.ois, 0);
                const totalCos   = primaryDsOsRows.reduce((s, r) => s + r.cos, 0);
                const totalOs    = primaryDsOsRows.reduce((s, r) => s + r.os, 0);
                const unassignedOs = primaryDsOsRows.find((r) => r.id === "unassigned")?.os ?? 0;
                const devTableOs = allDevelopersTotals.reduce((s, r) => s + r.os, 0);

                return (
                    <Segment raised style={{marginTop: "1rem"}}>
                        <Header
                            as="h4"
                            style={{display: "flex", alignItems: "center", flexWrap: "wrap", gap: "0.5rem"}}
                        >
                            Ticket pipeline &mdash; latest week ({latestWeekTag})
                            <Label color="blue" size="small">team view</Label>
                            {companyScopeTag && (
                                <Label color="red" size="small">{companyScopeTag}</Label>
                            )}
                        </Header>

                        <p style={{color: "#666", marginBottom: "0.75rem", fontSize: "0.95em"}}>
                            Each ticket belongs to exactly one status bucket. <strong>Total</strong> = true OS for the
                            week including unassigned.
                            {activeReports.length === 1 && devTableOs !== totalOs - unassignedOs && (
                                <span style={{color: "#db2828", marginLeft: 6}}>
                                    Dev totals gap: {Math.abs(totalOs - unassignedOs - devTableOs)} (tickets not yet in alias map)
                                </span>
                            )}
                        </p>

                        <div style={{overflowX: "auto"}}>
                            <Table compact celled striped unstackable>
                                <Table.Header>
                                    <Table.Row>
                                        <Table.HeaderCell>Status</Table.HeaderCell>
                                        <Table.HeaderCell textAlign="right">DEV</Table.HeaderCell>
                                        <Table.HeaderCell textAlign="right">OIS</Table.HeaderCell>
                                        <Table.HeaderCell textAlign="right">COS</Table.HeaderCell>
                                        <Table.HeaderCell textAlign="right">OS</Table.HeaderCell>
                                    </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                    {primaryDsOsRows.map((row) => (
                                        <Table.Row key={row.id} warning={row.id === "unassigned"}>
                                            <Table.Cell>{displayDsOsLabel(row.label)}</Table.Cell>
                                            <Table.Cell textAlign="right">{row.devDs}</Table.Cell>
                                            <Table.Cell textAlign="right">{row.ois}</Table.Cell>
                                            <Table.Cell textAlign="right">{row.cos}</Table.Cell>
                                            <Table.Cell textAlign="right">{row.os}</Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                                <Table.Footer>
                                    <Table.Row>
                                        <Table.HeaderCell><strong>Total (all tickets)</strong></Table.HeaderCell>
                                        <Table.HeaderCell textAlign="right"><strong>{totalDevDs}</strong></Table.HeaderCell>
                                        <Table.HeaderCell textAlign="right"><strong>{totalOis}</strong></Table.HeaderCell>
                                        <Table.HeaderCell textAlign="right"><strong>{totalCos}</strong></Table.HeaderCell>
                                        <Table.HeaderCell textAlign="right"><strong>{totalOs}</strong></Table.HeaderCell>
                                    </Table.Row>
                                </Table.Footer>
                            </Table>
                        </div>

                        {summaryDsOsRows.length > 0 && (
                            <>
                                <Header as="h5" style={{marginTop: "1.25rem", marginBottom: "0.25rem"}}>
                                    This-week highlights{" "}
                                    <span style={{color: "#888", fontWeight: 400, fontSize: "0.9em"}}>
                                        (sub-totals — already counted above, do not add to Total)
                                    </span>
                                </Header>
                                <div style={{overflowX: "auto"}}>
                                    <Table compact celled unstackable>
                                        <Table.Header>
                                            <Table.Row>
                                                <Table.HeaderCell>Highlight</Table.HeaderCell>
                                                <Table.HeaderCell textAlign="right">DEV</Table.HeaderCell>
                                                <Table.HeaderCell textAlign="right">OIS</Table.HeaderCell>
                                                <Table.HeaderCell textAlign="right">COS</Table.HeaderCell>
                                                <Table.HeaderCell textAlign="right">OS</Table.HeaderCell>
                                            </Table.Row>
                                        </Table.Header>
                                        <Table.Body>
                                            {summaryDsOsRows.map((row) => (
                                                <Table.Row key={row.id} positive>
                                                    <Table.Cell>{displayDsOsLabel(row.label)}</Table.Cell>
                                                    <Table.Cell textAlign="right">{row.devDs}</Table.Cell>
                                                    <Table.Cell textAlign="right">{row.ois}</Table.Cell>
                                                    <Table.Cell textAlign="right">{row.cos}</Table.Cell>
                                                    <Table.Cell textAlign="right">{row.os}</Table.Cell>
                                                </Table.Row>
                                            ))}
                                        </Table.Body>
                                    </Table>
                                </div>
                            </>
                        )}
                    </Segment>
                );
            })()}

            {/* ── Developer sections ── collapsed by default, team view is primary ──────────── */}
            <Segment raised style={{marginTop: "1rem"}}>
                <Header as="h4" style={{marginBottom: "0.5rem"}}>
                    <Icon name="users" />
                    Individual developer views
                    <span style={{fontSize: "0.85em", fontWeight: 400, color: "#888", marginLeft: 8}}>
                        (click to expand)
                    </span>
                </Header>
                <p style={{color: "#666", fontSize: "0.9em", marginBottom: "0.75rem"}}>
                    The sections below show per-developer breakdown. The team velocity above is the primary metric.
                </p>
                <Accordion fluid styled>

                    {/* 1 — Team trend per-developer line chart */}
                    <Accordion.Title
                        active={!!devAccordionOpen["trend"]}
                        onClick={() => toggleDevAccordion("trend")}
                    >
                        <Icon name="dropdown" />
                        Team trend — individual lines per developer
                    </Accordion.Title>
                    <Accordion.Content active={!!devAccordionOpen["trend"]}>
                        <div style={{paddingTop: "0.75rem"}}>
                        <Header
                        as="h5"
                        style={{display: "flex", alignItems: "center", flexWrap: "wrap", gap: "0.75rem", marginBottom: "0.5rem"}}
                    >
                        Team trend — all developers
                        <Label color={comparisonMetric === "devDs" ? "blue" : "green"} size="small">
                            {metricLabelText(comparisonMetric)}
                        </Label>
                        {companyScopeTag && (
                            <Label color="red" size="small">
                                {companyScopeTag}
                            </Label>
                        )}
                    </Header>
                    <p style={{color: "#666", marginBottom: "0.75rem"}}>
                        One line per developer per week. Use <strong>Metric</strong> above to switch DEV vs OS.
                    </p>
                    <ResponsiveContainer width="100%" height={420}>
                        <LineChart data={comparisonTrendData} margin={{top: 8, right: 16, left: 0, bottom: 0}}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                            <XAxis dataKey="label" tick={{fontSize: 11}} />
                            <YAxis tick={{fontSize: 11}} />
                            <Tooltip content={ComparisonTeamTooltip} />
                            <Legend wrapperStyle={{maxHeight: 140, overflowY: "auto", fontSize: 12}} />
                            {developerNames.map((name, i) => (
                                <Line
                                    key={name}
                                    type="monotone"
                                    dataKey={`v${i}`}
                                    name={name}
                                    stroke={COMPARISON_LINE_COLORS[i % COMPARISON_LINE_COLORS.length]}
                                    strokeWidth={2}
                                    dot={{r: 3}}
                                    isAnimationActive={reports.length < 20}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>

                        </div>
                    </Accordion.Content>

                    {/* 2 — All developers score totals table */}
                    <Accordion.Title
                        active={!!devAccordionOpen["totals"]}
                        onClick={() => toggleDevAccordion("totals")}
                    >
                        <Icon name="dropdown" />
                        All developers — score totals ({reports.length} week{reports.length !== 1 ? "s" : ""})
                    </Accordion.Title>
                    <Accordion.Content active={!!devAccordionOpen["totals"]}>
                        <div style={{paddingTop: "0.75rem"}}>
                    <Header as="h5" style={{display: "flex", alignItems: "center", flexWrap: "wrap", gap: "0.5rem"}}>
                        All developers — totals ({reports.length} week{reports.length !== 1 ? "s" : ""})
                        <Label color={comparisonMetric === "devDs" ? "blue" : "green"} size="small">
                            {metricLabelText(comparisonMetric)}
                        </Label>
                        {companyScopeTag && (
                            <Label color="red" size="small">
                                {companyScopeTag}
                            </Label>
                        )}
                    </Header>
                    <p style={{color: "#666", marginBottom: "0.75rem", fontSize: "0.95em"}}>
                        Scores <strong>summed</strong> across all <strong>{reports.length}</strong> loaded week
                        {reports.length !== 1 ? "s" : ""} ({weekRangeLabel || latestWeekTag}). Sorted by{" "}
                        <strong>{metricLabelText(comparisonMetric)}</strong>.
                    </p>
                    <div style={{overflowX: "auto"}}>
                        <Table compact celled striped unstackable selectable>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>Developer</Table.HeaderCell>
                                    <Table.HeaderCell textAlign="right">DEV</Table.HeaderCell>
                                    <Table.HeaderCell textAlign="right">OIS</Table.HeaderCell>
                                    <Table.HeaderCell textAlign="right">COS</Table.HeaderCell>
                                    <Table.HeaderCell textAlign="right">OS</Table.HeaderCell>
                                    <Table.HeaderCell textAlign="right">Tickets</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {allDevelopersTotals.map((row) => (
                                    <Table.Row key={row.developer}>
                                        <Table.Cell>
                                            <strong>{row.developer}</strong>
                                        </Table.Cell>
                                        <Table.Cell textAlign="right">{row.devDs}</Table.Cell>
                                        <Table.Cell textAlign="right">{row.ois}</Table.Cell>
                                        <Table.Cell textAlign="right">{row.cos}</Table.Cell>
                                        <Table.Cell textAlign="right">{row.os}</Table.Cell>
                                        <Table.Cell textAlign="right">{row.ticketCount}</Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                            <Table.Footer>
                                <Table.Row>
                                    <Table.HeaderCell>
                                        <strong>Total</strong>
                                    </Table.HeaderCell>
                                    <Table.HeaderCell textAlign="right">
                                        <strong>{allDevelopersTotals.reduce((s, r) => s + r.devDs, 0)}</strong>
                                    </Table.HeaderCell>
                                    <Table.HeaderCell textAlign="right">
                                        <strong>{allDevelopersTotals.reduce((s, r) => s + r.ois, 0)}</strong>
                                    </Table.HeaderCell>
                                    <Table.HeaderCell textAlign="right">
                                        <strong>{allDevelopersTotals.reduce((s, r) => s + r.cos, 0)}</strong>
                                    </Table.HeaderCell>
                                    <Table.HeaderCell textAlign="right">
                                        <strong>{allDevelopersTotals.reduce((s, r) => s + r.os, 0)}</strong>
                                    </Table.HeaderCell>
                                    <Table.HeaderCell textAlign="right">
                                        <strong>{allDevelopersTotals.reduce((s, r) => s + r.ticketCount, 0)}</strong>
                                    </Table.HeaderCell>
                                </Table.Row>
                            </Table.Footer>
                        </Table>
                    </div>
                        </div>
                    </Accordion.Content>

                    {/* 3 — Individual developer trend */}
                    <Accordion.Title
                        active={!!devAccordionOpen["single"]}
                        onClick={() => {
                            toggleDevAccordion("single");
                            // Auto-select first developer when opening if none selected
                            if (!devAccordionOpen["single"] &&
                                (selectedDeveloper === ALL_DEVELOPERS || !selectedDeveloper) &&
                                developerNames.length > 0) {
                                setSelectedDeveloper(developerNames[0]);
                            }
                        }}
                    >
                        <Icon name="dropdown" />
                        Individual developer trend
                    </Accordion.Title>
                    <Accordion.Content active={!!devAccordionOpen["single"]}>
                        <div style={{paddingTop: "0.75rem"}}>
                            <div style={{marginBottom: "1rem"}}>
                                <label style={{fontWeight: 600, display: "block", marginBottom: 6}}>Developer</label>
                                <Dropdown
                                    selection
                                    search
                                    placeholder="Select a developer"
                                    value={selectedDeveloper && selectedDeveloper !== ALL_DEVELOPERS ? selectedDeveloper : undefined}
                                    options={developerNames.map((d) => ({key: d, text: d, value: d}))}
                                    onChange={(_, d) => setSelectedDeveloper(d.value as string)}
                                />
                            </div>
                    <Header as="h5">Your trend — {selectedDeveloper && selectedDeveloper !== ALL_DEVELOPERS ? selectedDeveloper : "—"}</Header>
                    <ResponsiveContainer width="100%" height={340}>
                        <LineChart data={singleTrendData} margin={{top: 8, right: 16, left: 0, bottom: 0}}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                            <XAxis dataKey="label" tick={{fontSize: 11}} />
                            <YAxis tick={{fontSize: 11}} />
                            <Tooltip contentStyle={{fontSize: 12}} />
                            <Legend />
                            <Line type="monotone" dataKey="devDs" name="DEV" stroke="#2185d0" strokeWidth={2} />
                            <Line type="monotone" dataKey="os" name="OS (total)" stroke="#21ba45" strokeWidth={2} />
                            <Line type="monotone" dataKey="tickets" name="Tickets (count)" stroke="#f2711c" />
                        </LineChart>
                    </ResponsiveContainer>

                    {singleTrendData.length > 0 && (() => {
                        const totalDevDs = singleTrendData.reduce((s, r) => s + r.devDs, 0);
                        const totalOis = singleTrendData.reduce((s, r) => s + r.ois, 0);
                        const totalCos = singleTrendData.reduce((s, r) => s + r.cos, 0);
                        const totalOs = singleTrendData.reduce((s, r) => s + r.os, 0);
                        const totalTickets = singleTrendData.reduce((s, r) => s + r.tickets, 0);
                        return (
                            <div style={{overflowX: "auto", marginTop: "1rem"}}>
                                <Table compact celled striped unstackable>
                                    <Table.Header>
                                        <Table.Row>
                                            <Table.HeaderCell>Week</Table.HeaderCell>
                                            <Table.HeaderCell textAlign="right">DEV</Table.HeaderCell>
                                            <Table.HeaderCell textAlign="right">OIS</Table.HeaderCell>
                                            <Table.HeaderCell textAlign="right">COS</Table.HeaderCell>
                                            <Table.HeaderCell textAlign="right">OS</Table.HeaderCell>
                                            <Table.HeaderCell textAlign="right">Tickets</Table.HeaderCell>
                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                        {[...singleTrendData].reverse().map((row) => (
                                            <Table.Row key={row.label} positive={row.label === latestWeekTag}>
                                                <Table.Cell>
                                                    <strong>{row.label}</strong>
                                                    {row.label === latestWeekTag && (
                                                        <Label size="mini" color="blue" style={{marginLeft: 6}}>
                                                            latest
                                                        </Label>
                                                    )}
                                                </Table.Cell>
                                                <Table.Cell textAlign="right">{row.devDs}</Table.Cell>
                                                <Table.Cell textAlign="right">{row.ois}</Table.Cell>
                                                <Table.Cell textAlign="right">{row.cos}</Table.Cell>
                                                <Table.Cell textAlign="right">{row.os}</Table.Cell>
                                                <Table.Cell textAlign="right">{row.tickets}</Table.Cell>
                                            </Table.Row>
                                        ))}
                                    </Table.Body>
                                    <Table.Footer>
                                        <Table.Row>
                                            <Table.HeaderCell>
                                                <strong>Total ({singleTrendData.length} week{singleTrendData.length !== 1 ? "s" : ""})</strong>
                                            </Table.HeaderCell>
                                            <Table.HeaderCell textAlign="right">
                                                <strong>{totalDevDs}</strong>
                                            </Table.HeaderCell>
                                            <Table.HeaderCell textAlign="right">
                                                <strong>{totalOis}</strong>
                                            </Table.HeaderCell>
                                            <Table.HeaderCell textAlign="right">
                                                <strong>{totalCos}</strong>
                                            </Table.HeaderCell>
                                            <Table.HeaderCell textAlign="right">
                                                <strong>{totalOs}</strong>
                                            </Table.HeaderCell>
                                            <Table.HeaderCell textAlign="right">
                                                <strong>{totalTickets}</strong>
                                            </Table.HeaderCell>
                                        </Table.Row>
                                    </Table.Footer>
                                </Table>
                            </div>
                        );
                    })()}
                        </div>
                    </Accordion.Content>

                    {/* 4 — Git activity per developer */}
                    <Accordion.Title
                        active={!!devAccordionOpen["activity"]}
                        onClick={() => toggleDevAccordion("activity")}
                    >
                        <Icon name="dropdown" />
                        Latest week — git activity (test files &amp; unique files)
                    </Accordion.Title>
                    <Accordion.Content active={!!devAccordionOpen["activity"]}>
                        {latestDevelopersActivity.length > 0 && (
                        <div style={{padding: "0.75rem"}}>
                            <p style={{color: "#666", marginBottom: "1rem", fontSize: "0.95em"}}>
                                Test files: distinct <code>.kt</code> paths under <code>/test/</code> touched this week.
                                Unique files: distinct source paths by top-level project.
                            </p>
                            <Grid stackable columns={2}>
                                <Grid.Column>
                                    <Header as="h5">Test files touched</Header>
                                    <div style={{overflowX: "auto"}}>
                                        <Table compact celled striped unstackable>
                                            <Table.Header>
                                                <Table.Row>
                                                    <Table.HeaderCell>Developer</Table.HeaderCell>
                                                    <Table.HeaderCell textAlign="right">Count</Table.HeaderCell>
                                                </Table.Row>
                                            </Table.Header>
                                            <Table.Body>
                                                {latestDevelopersActivity.map((row) => (
                                                    <Table.Row key={row.developer}>
                                                        <Table.Cell><strong>{row.developer}</strong></Table.Cell>
                                                        <Table.Cell textAlign="right">{row.testsTouched}</Table.Cell>
                                                    </Table.Row>
                                                ))}
                                            </Table.Body>
                                        </Table>
                                    </div>
                                </Grid.Column>
                                <Grid.Column>
                                    <Header as="h5">Unique files by project</Header>
                                    <div style={{overflowX: "auto"}}>
                                        {uniqueFilesFlatRows.length === 0 ? (
                                            <Message info size="small">
                                                No per-project file breakdown. Re-export with the latest stats tool.
                                            </Message>
                                        ) : (
                                            <Table compact celled striped unstackable>
                                                <Table.Header>
                                                    <Table.Row>
                                                        <Table.HeaderCell>Developer</Table.HeaderCell>
                                                        <Table.HeaderCell>Project</Table.HeaderCell>
                                                        <Table.HeaderCell textAlign="right">Files</Table.HeaderCell>
                                                    </Table.Row>
                                                </Table.Header>
                                                <Table.Body>
                                                    {uniqueFilesFlatRows.map((row, idx) => (
                                                        <Table.Row key={`${row.developer}-${row.project}-${idx}`}>
                                                            <Table.Cell><strong>{row.developer}</strong></Table.Cell>
                                                            <Table.Cell>{row.project}</Table.Cell>
                                                            <Table.Cell textAlign="right">{row.fileCount}</Table.Cell>
                                                        </Table.Row>
                                                    ))}
                                                </Table.Body>
                                            </Table>
                                        )}
                                    </div>
                                </Grid.Column>
                            </Grid>
                        </div>
                        )}
                    </Accordion.Content>

                </Accordion>
            </Segment>

        </div>
    );
}
